import React, {useMemo, useState} from 'react'
import Text from '../../../../components/text/Text'
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts'
import {ILeaderGraph} from '../../../../apis/leader/getLeaderGraph'
import {useDisplayNumber} from '../../../../utils/number'

interface IProps {
    className?: string
    data?: ILeaderGraph[]
}

const LeaderChartTWR = ({className, data}: IProps) => {
    const {renderPercent, floorBalance, displayAmount} = useDisplayNumber()
    const renderDate = when => {
        const date = String(when)
        const year = date?.substring(0, 4)
        const month = date?.substring(4, 6)
        const day = date?.substring(6, 8)

        return `${year}.${month}.${day}`
    }
    const gradientOffset = () => {
        const arr = data?.length > 0 ? data : []
        const dataMax = Math.max(...arr?.map(i => i.twr))
        const dataMin = Math.min(...arr?.map(i => i.twr))

        if (dataMax <= 0) {
            return 0
        }
        if (dataMin >= 0) {
            return 1
        }

        return dataMax / (dataMax - dataMin)
    }

    const off = gradientOffset()

    const CustomTooltip = ({active, payload, label}: any) => {
        if (active && payload && payload.length) {
            const x = payload[0].payload.when
            const y = payload[0].payload.twr

            return (
                <div className="px-6 py-3 bg-white rounded-lg shadow-xl">
                    <p className={'font-sans text-body3 text-font03'}>{renderDate(x)}</p>
                    <p
                        className={`font-sans text-h3 ${
                            y > 0 ? 'text-font_green' : y < 0 ? 'text-font_red' : 'text-font03'
                        }`}>
                        {renderPercent(y, 1000)}
                    </p>
                </div>
            )
        }
        return null
    }

    const [activeDotColor, setActiveDotColor] = useState('#000000')

    return (
        <div>
            <Text className={'bg-line03 py-[7px] rounded-[5px]'}>TWR (시간가중 수익률)</Text>
            <ResponsiveContainer width={'99%'} height={400} className={'mt-[50px]'}>
                <AreaChart
                    onMouseMove={(props: any) =>
                        props?.activePayload?.at(0)?.payload?.twr > 0
                            ? setActiveDotColor('#0AAF82')
                            : props?.activePayload?.at(0)?.payload?.twr === 0
                            ? setActiveDotColor('#8A8F95')
                            : setActiveDotColor('#D62343')
                    }
                    data={data}
                    margin={{
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                    }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="when"
                        tick={props => {
                            const isFirstChild = props?.index === 0
                            const isLastChild = props?.index === props?.ticks?.length - 1

                            const textAnchor = isFirstChild ? 'start' : isLastChild ? 'end' : 'middle'

                            return (
                                <text
                                    x={props?.x}
                                    y={props?.y}
                                    fontSize={12}
                                    textAnchor={textAnchor}
                                    fill="#8A8F95"
                                    dy={15}>
                                    {renderDate(props?.payload?.value)}
                                </text>
                            )
                        }}
                        ticks={
                            data?.length < 10
                                ? []
                                : [
                                      data?.at(0)?.when,
                                      data?.at(Math.floor(data?.length * 0.25))?.when,
                                      data?.at(Math.floor(data?.length * 0.5))?.when,
                                      data?.at(Math.floor(data?.length * 0.75))?.when,
                                      data?.at(-1)?.when,
                                  ]
                        }
                    />
                    <YAxis
                        tick={props => {
                            return (
                                <svg y={1} textAnchor={'end'} dominantBaseline={'middle'} dx={20}>
                                    <text x={props?.x} y={props?.y} fill={'#8A8F95'} fontSize="12px">
                                        {props?.payload?.value}%
                                    </text>
                                </svg>
                            )
                        }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={true} />
                    <defs>
                        <linearGradient id="splitColor_twr" x1="0" y1="0" x2="0" y2="1">
                            <stop offset={off} stopColor="#0AAF82" stopOpacity={0.5} />
                            <stop offset={0} stopColor="#FFFFFF" stopOpacity={1} />
                            <stop offset={off} stopColor="#E6434E" stopOpacity={0.7} />
                        </linearGradient>
                    </defs>
                    <Area
                        type="monotone"
                        dataKey="twr"
                        stroke="url(#colorUv_twr)"
                        fill="url(#splitColor_twr)"
                        strokeWidth={1.5}
                        activeDot={{fill: activeDotColor}}
                    />
                    <defs>
                        <linearGradient id="colorUv_twr" x1="0" y1="0" x2="0" y2="1">
                            <stop offset={off} stopColor="#0AAF82" stopOpacity={1} />
                            <stop offset={off} stopColor="#D62343" stopOpacity={1} />
                        </linearGradient>
                    </defs>
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

export default React.memo(LeaderChartTWR)
